import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Alert, CircularProgress, List, ListItem, ListItemText, Divider, Grid } from '@mui/material';
import { getPlannedSchedule } from '../../services/scheduleService';
import { format } from 'date-fns'; // Using date-fns for timestamp formatting

const CampaignScheduleView = () => {
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSchedule = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getPlannedSchedule();
        if (data) {
          setScheduleData(data);
        } else {
          setError('No schedule data found. Please ensure preferences have been set and calculated.');
        }
      } catch (err) {
        console.error("Error fetching campaign schedule:", err);
        setError('Failed to load campaign schedule. Please try again later.');
      }
      setLoading(false);
    };

    fetchSchedule();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress sx={{ color: '#d98d2a' }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!scheduleData) {
    return <Alert severity="info" sx={{ m: 2 }}>No campaign schedule has been calculated yet.</Alert>;
  }

  const { bestCalculatedDay, availabilityScore, vetoes, participantCount, lastUpdated, preferenceScores } = scheduleData;

  const formatTimestamp = (timestamp) => {
    if (timestamp && timestamp.seconds) {
      return format(new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000), 'PPPppp');
    }
    return 'Not available';
  };

  return (
    <Paper elevation={3} sx={{ m: 'auto', mt: 4, p: 3, maxWidth: 800, bgcolor: '#333', color: '#fff' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif', textAlign: 'center' }}>
        Campaign Schedule Overview
      </Typography>

      {bestCalculatedDay ? (
        <Typography variant="h5" sx={{ color: '#4CAF50', textAlign: 'center', my: 2, fontFamily: '"Georgia", serif' }}>
          Best Calculated Day for Session: <strong style={{ color: '#66BB6A'}}>{bestCalculatedDay}</strong>
        </Typography>
      ) : (
        <Typography variant="h6" sx={{ color: '#FFC107', textAlign: 'center', my: 2, fontFamily: '"Georgia", serif' }}>
          No single best day could be determined based on current preferences and vetoes.
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif' }}>
            Daily Availability Scores
          </Typography>
          <List dense sx={{ bgcolor: '#424242', borderRadius: 1 }}>
            {Object.entries(availabilityScore || {}).map(([day, score]) => (
              <ListItem key={day}>
                <ListItemText primary={`${day}: ${score} available`} sx={{ color: '#F5F5F5' }} />
              </ListItem>
            ))}
          </List>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif' }}>
            Vetoed Days
          </Typography>
          <List dense sx={{ bgcolor: '#424242', borderRadius: 1 }}>
            {Object.entries(vetoes || {}).map(([day, userIds]) => 
              userIds.length > 0 && (
                <ListItem key={day}>
                  <ListItemText 
                    primary={`${day}: Vetoed by ${userIds.length} player(s)`} 
                    // secondary={`IDs: ${userIds.join(', ')}`} // Optionally show user IDs if needed for admin
                    sx={{ color: '#F5F5F5', '& .MuiListItemText-secondary': { color: '#BDBDBD' } }} 
                  />
                </ListItem>
              )
            )}
            {Object.values(vetoes || {}).every(arr => arr.length === 0) && (
                <ListItem>
                    <ListItemText primary="No days have been vetoed." sx={{ color: '#F5F5F5' }} />
                </ListItem>
            )}
          </List>
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 3, borderColor: '#555' }} />

      <Typography variant="h6" gutterBottom sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif', mt: 3 }}>
        Daily Preference Scores
      </Typography>
      <List dense sx={{ bgcolor: '#424242', borderRadius: 1, mb: 3 }}>
        {preferenceScores && Object.entries(preferenceScores).map(([day, score]) => (
          <ListItem key={day}>
            <ListItemText primary={`${day}: ${score} points`} sx={{ color: '#F5F5F5' }} />
          </ListItem>
        ))}
        {(!preferenceScores || Object.keys(preferenceScores).length === 0) && (
            <ListItem>
                <ListItemText primary="No preference scores recorded." sx={{ color: '#F5F5F5' }} />
            </ListItem>
        )}
      </List>

      <Typography variant="body1" sx={{ color: '#F5F5F5', mt: 1, fontFamily: '"Georgia", serif' }}>
        Total Participants Considered: <strong>{participantCount}</strong>
      </Typography>
      <Typography variant="body2" sx={{ color: '#BDBDBD', mt: 1, fontFamily: '"Georgia", serif' }}>
        Schedule Last Updated: {formatTimestamp(lastUpdated)}
      </Typography>
    </Paper>
  );
};

export default CampaignScheduleView;
